import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Translate, I18n } from 'react-redux-i18n';
import { config } from '../../config';
import { getApiHeaders } from '../../api/apiHeaders';
import './FearGreedIndex.css';

const RATING_KEYS = {
    extreme_fear: 'fear_greed_extreme_fear',
    fear: 'fear_greed_fear',
    neutral: 'fear_greed_neutral',
    greed: 'fear_greed_greed',
    extreme_greed: 'fear_greed_extreme_greed',
};

function ratingColor(rating) {
    switch (rating) {
        case 'extreme_fear':
            return '#ef4444';
        case 'fear':
            return '#f97316';
        case 'neutral':
            return '#eab308';
        case 'greed':
            return '#22c55e';
        case 'extreme_greed':
            return '#10b981';
        default:
            return '#94a3b8';
    }
}

/** Semicircle gauge: score 0–100 → needle angle from 180° (left) to 0° (right). */
function needlePoint(score, cx, cy, r) {
    const clamped = Math.max(0, Math.min(100, Number(score) || 0));
    const angleDeg = 180 - (clamped / 100) * 180;
    const rad = (angleDeg * Math.PI) / 180;
    return {
        x: cx + r * Math.cos(rad),
        y: cy - r * Math.sin(rad),
    };
}

const FearGreedIndex = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await axios.get(`${config.api.web}market/fear-greed`, {
                    headers: getApiHeaders(),
                });
                if (cancelled) {
                    return;
                }
                if (response.status < 300 && response.data?.status === 'Success') {
                    setData(response.data.body);
                } else {
                    setError(true);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const score = Number(data?.score) || 0;
    const rating = data?.rating || 'neutral';
    const color = ratingColor(rating);
    const tip = needlePoint(score, 100, 100, 72);
    const change = Number(data?.change) || 0;

    return (
        <div className="fear-greed">
            <div className="fear-greed-header">
                <h3 className="fear-greed-title">
                    <Translate value="fear_greed_title" />
                </h3>
                <p className="fear-greed-subtitle">
                    <Translate value="fear_greed_subtitle" />
                </p>
            </div>

            {loading && (
                        <p className="fear-greed-status">
                            <Translate value="fear_greed_loading" />
                        </p>
            )}

            {!loading && error && (
                <p className="fear-greed-status fear-greed-status--error">
                    <Translate value="try_later" />
                </p>
            )}

            {!loading && !error && data && (
                <>
                    <div className="fear-greed-gauge-wrap" aria-hidden="true">
                        <svg
                            className="fear-greed-gauge"
                            viewBox="0 0 200 120"
                            role="img"
                            aria-label={I18n.t(RATING_KEYS[rating] || RATING_KEYS.neutral)}
                        >
                            <defs>
                                <linearGradient id="fearGreedArc" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#ef4444" />
                                    <stop offset="25%" stopColor="#f97316" />
                                    <stop offset="50%" stopColor="#eab308" />
                                    <stop offset="75%" stopColor="#22c55e" />
                                    <stop offset="100%" stopColor="#10b981" />
                                </linearGradient>
                            </defs>
                            <path
                                d="M 20 100 A 80 80 0 0 1 180 100"
                                fill="none"
                                stroke="rgba(148, 163, 184, 0.2)"
                                strokeWidth="14"
                                strokeLinecap="round"
                            />
                            <path
                                d="M 20 100 A 80 80 0 0 1 180 100"
                                fill="none"
                                stroke="url(#fearGreedArc)"
                                strokeWidth="14"
                                strokeLinecap="round"
                            />
                            <line
                                x1="100"
                                y1="100"
                                x2={tip.x}
                                y2={tip.y}
                                stroke={color}
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                            <circle cx="100" cy="100" r="6" fill={color} />
                            <circle cx="100" cy="100" r="3" fill="#0f172a" />
                        </svg>
                        <div className="fear-greed-score" style={{ color }}>
                            {score}
                        </div>
                    </div>

                    <div className="fear-greed-label" style={{ color }}>
                        <Translate value={RATING_KEYS[rating] || RATING_KEYS.neutral} />
                    </div>

                    <div className="fear-greed-meta">
                        <span>
                            <Translate value="fear_greed_prev_close" />
                            {': '}
                            <strong>{data.previous_close}</strong>
                        </span>
                        <span className={change >= 0 ? 'fear-greed-up' : 'fear-greed-down'}>
                            {change >= 0 ? '+' : ''}
                            {change}
                        </span>
                    </div>

                    <div className="fear-greed-scale">
                        <span><Translate value="fear_greed_fear" /></span>
                        <span><Translate value="fear_greed_greed" /></span>
                    </div>
                </>
            )}
        </div>
    );
};

export default FearGreedIndex;
